import { AccentVariantEnum } from '@delmonicos/shared-design-system-react/src/components/accent-icon/accent-icon';
import {
  BarChart,
  BarChartTheme,
  BarColorVariant,
} from '@delmonicos/shared-design-system-react/src/components/bar-chart/bar-chart';
import { Card } from '@delmonicos/shared-design-system-react/src/components/card/card';
import {
  GapEnum,
  Stack,
} from '@delmonicos/shared-design-system-react/src/components/stack/stack';
import {
  Text,
  TextFontSize,
} from '@delmonicos/shared-design-system-react/src/components/typography/text/text';
import { AccentTitle } from '@delmonicos/shared-design-system-react/src/components/typography/title/accent-title/accent-title';
import { TransactionReportsStatisticsByDateModel } from '@delmonicos/shared-sdk/libs/cpms/src/libs/charge-hub-service-lib/src/wrapper/outputs/transaction-report/transaction-reports-statistics-by-date.model';
import { addMonths, format } from 'date-fns';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

export enum ByDateCardVariantEnum {
  Transactions = 'transactions',
  Revenue = 'revenue',
  Energy = 'energy',
}

interface ByDateCardProps {
  data?: TransactionReportsStatisticsByDateModel;
  variant?: ByDateCardVariantEnum;
}

interface ByDateVariation {
  name: string;
  theme: BarChartTheme;
  titleVariant: AccentVariantEnum;
}

const byDateVariations: Record<ByDateCardVariantEnum, ByDateVariation> = {
  [ByDateCardVariantEnum.Transactions]: {
    name: 'transactions',
    theme: BarChartTheme.PRIMARY,
    titleVariant: AccentVariantEnum.Primary,
  },
  [ByDateCardVariantEnum.Revenue]: {
    name: 'revenue',
    theme: BarChartTheme.DECORATIVE,
    titleVariant: AccentVariantEnum.Accent1,
  },
  [ByDateCardVariantEnum.Energy]: {
    name: 'energy',
    theme: BarChartTheme.SECONDARY,
    titleVariant: AccentVariantEnum.Secondary,
  },
};

export const ByDateCard = ({
  data,
  variant = ByDateCardVariantEnum.Transactions,
}: ByDateCardProps): React.JSX.Element => {
  const { t } = useTranslation();
  const variation = byDateVariations[variant];

  const series = useMemo(() => {
    if (!data?.value) return [];

    const currentMonth = format(new Date(), 'yyyy-MM');
    const nextMonth = format(addMonths(new Date(), 1), 'yyyy-MM');

    return data.value.map((d: any) => {
      let colorVariant: BarColorVariant = BarColorVariant.BASIC;

      if (d.date === currentMonth) {
        colorVariant = BarColorVariant.TOP1;
      } else if (d.date === nextMonth) {
        colorVariant = BarColorVariant.OTHERS;
      }

      return {
        name: d.date,
        value: d.total ? Number(d.total.toFixed(2)) : 0,
        colorVariant,
      };
    });
  }, [data]);

  return (
    <Card gap={GapEnum.M} flex={1}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <AccentTitle
          icon="calendar-date"
          text={t('$.statistics.cards.by-date.title')}
          variant={variation.titleVariant}
        />
        <Text fontSize={TextFontSize.Large}>
          {t(`$.statistics.cards.by-date.subtitle.${variation.name}`)}
        </Text>
      </Stack>

      <BarChart series={series} theme={variation.theme} showValue />
    </Card>
  );
};
